import { StyleSheet } from 'react-native';
import { tokens } from '../design-system/tokens';

// Figma reference: 3412:5136 (1-on-1) and 3412:5207 (group). All
// values measured from `Frame 1000004421`/`5076`/`4422` (bubble outer)
// and its inner `Frame 1000005067` (content) — see
// `tmp/figma-chatroom-redo/extracted.md` Section B for the JSON dump.
//
// Net bubble paddings: 4 outer + 6 inner (horizontal) = 10 total text
// inset; 4 outer (vertical) = 4 total. Bubble radius is 16 on ALL
// four corners — Figma does NOT apply the "speech bubble corner squared"
// trick to the active-author side, so we drop the old
// `borderBottomRightRadius: xs` / `borderBottomLeftRadius: xs` shape.
//
// Body font: Lato 400 16/22.4 (NOT the tokens.body=17 default). The
// previous baseline multiplied `body(17) * lineHeight.normal(1.4) = 23.8`,
// which combined with `paddingVertical: spacing.sm (8)` made compact
// "Yo"-style bubbles look squashed at the top/bottom while still being
// tall overall. Hard-coding 16/22 here matches Figma and trims the
// vertical envelope by ~6px per bubble.

const BUBBLE_OUTER_PADDING = 4;
const BUBBLE_INNER_PADDING_H = 6;
const BUBBLE_BODY_LINE_HEIGHT = 22;
const BUBBLE_BODY_FONT_SIZE = 16;
const BUBBLE_META_LINE_HEIGHT = 14;
const BUBBLE_META_FONT_SIZE = 10;
const BUBBLE_RADIUS = 16;

export const styles = StyleSheet.create({
  rowSelf: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginVertical: tokens.spacing.xxs,
  },
  rowOther: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    marginVertical: tokens.spacing.xxs,
  },
  avatarGutter: {
    width: 36,
    marginRight: tokens.spacing.sm,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: 2,
  },
  bubble: {
    // CRITICAL: shrink-to-content. The previous `maxWidth: '75%'` on
    // BOTH the bubble and the column collapsed short bubbles into a
    // tiny vertical column where "7:00 PM" wrapped to two lines.
    // `alignSelf: 'flex-start'` lets the bubble size to its inline
    // content's intrinsic width (capped at 75% of `bubbleColumn` via
    // that node's maxWidth).
    alignSelf: 'flex-start',
    // Positioning context for the absolutely-positioned speech tail
    // (BubbleTail) so it anchors to THIS bubble's bottom-outer corner
    // rather than some distant ancestor.
    position: 'relative',
    // Figma `Frame 1000004421` — uniform padding 4 on all sides.
    paddingHorizontal: BUBBLE_OUTER_PADDING,
    paddingVertical: BUBBLE_OUTER_PADDING,
    borderRadius: BUBBLE_RADIUS,
  },
  // Self bubble (right column) needs to pin its right edge.
  bubbleSelf: { alignSelf: 'flex-end' },
  bubbleOther: { alignSelf: 'flex-start' },
  // Inner content frame — Figma `Frame 1000005067`. 10px net horizontal
  // text inset (4 outer + 6 inner). 2px vertical gap so a stacked
  // reply preview + body sit with a hairline of breathing room.
  innerContent: {
    flexDirection: 'column',
    paddingHorizontal: BUBBLE_INNER_PADDING_H,
    gap: 2,
  },
  // INLINE LAYOUT — body text + timestamp + read-receipt in a single
  // wrapping row, baseline-aligned. This is the iMessage / WhatsApp
  // pattern: short messages put time on the same line as the body;
  // long messages have time at the trailing edge of the last line; if
  // the time can't fit alongside the last word it wraps to a new row.
  // Crucially `flex-wrap` with all leaf Text nodes as siblings makes
  // the bubble width hug whichever LINE happens to be longest — so a
  // "Hi" bubble naturally widens enough to fit "Hi 7:00 PM" on one row.
  bodyContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  bodyText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: BUBBLE_BODY_FONT_SIZE,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: BUBBLE_BODY_LINE_HEIGHT,
  },
  // Spacer between the last body chunk and the inline timestamp.
  // Rendered as an empty Text inside `bodyContainer` so it participates
  // in the flex-wrap calculation.
  metaSpacer: {
    width: 8,
  },
  // Figma 7038:2000 "@All" pill — radius 4, horizontal padding 4.
  mentionText: {
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  // Reply quote — Figma 3412:5059 "Reply" instance. A nested rounded
  // inset card INSIDE the bubble (NOT a left-border strip): r=12,
  // pad=4, inner text frame pad H=8, gap=2. Background set at runtime
  // (#000000@0.20 over the indigo own bubble / #FFFFFF@0.10 over the
  // dark other bubble). Holds an optional quoted-sender header + the
  // preview line.
  replyCard: {
    borderRadius: 12,
    padding: 4,
    marginBottom: 2,
    gap: 2,
  },
  replyCardInner: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 2,
  },
  // Row wrapper so a media reply shows a thumbnail left of the quote text.
  replyCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  replyThumbWrap: {
    width: 36,
    height: 36,
    borderRadius: 8,
    overflow: 'hidden',
  },
  replyThumb: {
    width: 36,
    height: 36,
  },
  // Play glyph overlay for a video reply thumbnail.
  replyThumbPlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.28)',
  },
  replyThumbPlayGlyph: {
    color: '#FFFFFF',
    fontSize: 12,
  },
  replyQuoteName: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    lineHeight: 16,
  },
  replyText: {
    fontSize: 14,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 19,
  },
  // Group sender name rendered INSIDE the first bubble of an
  // other-sender run (Figma 3412:5207 Frame 1000004564) — sits above
  // the body, #B5BAC3 (text.secondary) 12/16.8 w700.
  // Group sender header row — name + optional verified badge (Figma
  // 7038:2148). Horizontal so the BadgeCheck sits inline after the name.
  senderNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 2,
  },
  senderNameInner: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    lineHeight: 17,
  },
  // Inline meta — sits at the trailing edge of the last body line.
  // Baseline-aligned with the body text via the parent's
  // `alignItems: 'flex-end'`. Renders as a `<Text>` (NOT a `<View>`)
  // so it participates in the same inline-wrap layout as the body
  // chunks. The slight `transform: translateY(2)` would optically
  // settle the small caption text on the body's baseline — but we
  // keep it un-transformed for cross-platform fidelity.
  timestampText: {
    fontSize: BUBBLE_META_FONT_SIZE,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: BUBBLE_META_LINE_HEIGHT,
    // Pull the small caption down so it baselines with the body text.
    // Without this RN's flex-end aligns the box bottoms, leaving the
    // smaller line floating above the body's optical baseline.
    paddingBottom: 2,
  },
  readReceiptText: {
    fontSize: BUBBLE_META_FONT_SIZE,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: BUBBLE_META_LINE_HEIGHT,
    paddingBottom: 2,
  },
  bubbleColumn: {
    flexDirection: 'column',
    // 75% cap is the OUTER constraint; the inner bubble shrinks-to-
    // content via `alignSelf` (see `bubble`). For self-aligned columns
    // the column hugs the right edge.
    maxWidth: '75%',
  },
  // Self column: pin every child (bubble, edited/error rows, thread
  // preview card) to the RIGHT. Without this a SMALL self bubble sitting
  // above a WIDE child (e.g. a ThreadPreviewCard) gets left-aligned —
  // the column widens to the card and the inline-block bubble defaults to
  // the column's left edge. `alignItems: flex-end` keeps the bubble on
  // the right regardless of the widest sibling.
  bubbleColumnSelf: {
    alignItems: 'flex-end',
  },
  // Sender header for group chats (3412:5207 — `Frame 1000004425`).
  // Sits OUTSIDE the bubble, above it. 8px horizontal padding aligns
  // the sender label with the bubble's net text inset (4 outer + 6
  // inner = 10, padded to 8 in Figma — close enough for parity).
  senderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: tokens.spacing.sm,
    marginBottom: 2,
  },
  senderName: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    lineHeight: 16,
  },
  failureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: tokens.spacing.xxs,
    gap: tokens.spacing.xs,
  },
  failureText: {
    fontSize: BUBBLE_META_FONT_SIZE,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: BUBBLE_META_LINE_HEIGHT,
  },
  retryButton: {
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xxs,
    borderRadius: tokens.radius.xs,
  },
  retryText: {
    fontSize: BUBBLE_META_FONT_SIZE,
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    lineHeight: BUBBLE_META_LINE_HEIGHT,
    textDecorationLine: 'underline',
  },
  // Pinned footer — Figma 3412:5095. Sits below the bubble: a 12px
  // pin glyph + "Message pinned" (#B5BAC3 / text.secondary, 12/17 w400).
  pinnedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: tokens.spacing.sm,
    marginTop: 2,
  },
  pinnedLabel: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 17,
  },
  // Per-message send-error footer — Figma 3412:4643. "Error sending
  // message" (rose 10/14 w400) + a 10px info-circle, left-aligned
  // below the bubble.
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    paddingHorizontal: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxs,
    marginTop: 2,
  },
  errorText: {
    fontSize: 10,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 14,
  },
  // Standalone "(edited)" line below the body — Figma 2869:5218.
  // text.secondary (#B5BAC3), 12/17 w400, NOT italic, NOT inline.
  editedLine: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 17,
    marginTop: 1,
    paddingHorizontal: tokens.spacing.sm,
  },
  // Standalone "(forwarded)" line below the body — mirrors editedLine
  // (text.secondary, 12/17 w400, under the bubble, NOT inline).
  forwardedLine: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 17,
    marginTop: 1,
    paddingHorizontal: tokens.spacing.sm,
  },
  // Bubble tail — Figma "Polygon 3". A small rounded square rotated
  // 45° anchored just OUTSIDE the bottom corner of the LAST bubble in a
  // same-sender run, filled with the bubble color. Its inner half
  // overlaps the bubble's rounded corner (same fill → fills the notch),
  // its outer tip pokes past the edge to form the speech tail. Drawn on
  // top (no negative z-index, which RN-Web hides behind the parent fill)
  // and offset horizontally so it never overlaps the inline timestamp.
  // (Speech-tail positioning is expressed via NativeWind classes on the
  // wrapper View in MessageBubble.tsx — see `<BubbleTail>`.)
  // Thread "View Thread" affordance — Figma 2938:10511. Below the
  // bubble, accent-indigo text link.
  threadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    // paddingHorizontal: tokens.spacing.sm,
    marginTop: 4,
  },
  threadLink: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.bold}` as any,
    lineHeight: 16,
  },
  // Hover-affordance container. Sits above the bubble in document
  // flow with `position: 'relative'` so the absolutely-positioned
  // strip anchors against the bubble's bounding box.
  bubbleHoverHost: {
    position: 'relative',
  },
  // Hover-actions strip — Figma `2804:27483 → Action panel`.
  //   fill        #15171C  (dark surface tertiary; light pair below)
  //   radius      20
  //   padding     L8 / R8 / T4 / B4
  //   gap         8
  //   height      34
  //
  // Position: top above the bubble with a 4px gap. The two variants
  // (`hoverStripSelf`, `hoverStripOther`) anchor the strip to the
  // opposite side of the bubble so it floats over empty chat space.
  hoverStrip: {
    position: 'absolute',
    top: -38,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    // `backgroundColor` applied at runtime via `useColorScheme` in
    // `MessageBubble.tsx` so the light/dark pairing (#F5F6F8 / #15171C)
    // lands on the right scheme. Light pair is theme.colors.surface
    // .tertiary; dark hex is the Figma `Action panel` fill.
    //
    // Subtle elevation. Figma `Action panel` has `effects: []`, but
    // floating over a non-uniform background needs a contrast cue —
    // a 12% black shadow at 8/16 offset is the canonical "card lifts
    // off chat" treatment used by `2804:27521` (CallDropdownMenu).
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 4,
    zIndex: 10,
  },
  hoverStripSelf: {
    // Self bubble is right-aligned; place strip at the top-left.
    left: 0,
  },
  hoverStripOther: {
    // Other bubble is left-aligned; place strip at the top-right.
    right: 0,
  },
  hoverButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 4,
    paddingVertical: 4,
    borderRadius: 8,
  },
  hoverIconGlyph: {
    fontSize: 14,
    lineHeight: 16,
    // Color injected at runtime (theme.colors.text.tertiary). Light
    // pair: `#8B92A0`, dark: `#8B92A0` (same on both for parity with
    // Figma's `Vector` stroke #8C92A0).
  },
  hoverLabel: {
    fontSize: 12,
    fontWeight: `${tokens.typography.fontWeight.regular}` as any,
    lineHeight: 16,
    // Color injected at runtime (theme.colors.text.primary). Light:
    // `#0B0C0F` / dark: `#F5F6F8` per Figma label `#F5F6F8`.
  },
});
